import type { Sim, Snapshot, VisualEvent } from './sim';

/**
 * The host's one-shot events (callouts, blasts, shakes, damage numbers, the new-map banner) have two
 * readers: the host's own screen, which drains them every frame, and the viewers, who get them inside
 * snapshots, which go out only every few ticks. If the sim's queue is handed to whoever asks first, a
 * viewer only gets the events that happened to be queued on a snapshot tick (about one in four at one
 * tick per frame) and the host's frame drain takes the rest. Taking the queue after every step and
 * keeping a copy for each reader gives both of them every event exactly once, in order.
 */
export class VisualFeed {
  private local: VisualEvent[] = [];
  private net: VisualEvent[] = [];

  /** Takes what the sim has queued since the last call; `networked` says whether viewers are waiting for it. */
  collect(sim: Pick<Sim, 'drainVisual'>, networked: boolean): void {
    const events = sim.drainVisual();
    if (events.length === 0) return;
    this.local.push(...events);
    if (networked) this.net.push(...events);
  }

  /**
   * After a sim step: collect, and on a snapshot tick hand back the snapshot to broadcast, carrying every
   * event since the last one. Null when there is nothing to send this tick.
   */
  afterStep(sim: Pick<Sim, 'drainVisual' | 'snapshot'>, networked: boolean, snapshotTick: boolean): Snapshot | null {
    this.collect(sim, networked);
    if (!networked || !snapshotTick) return null;
    const snap = sim.snapshot();
    snap.visual = this.net.splice(0);
    return snap;
  }

  /** What the host's own renderer and sounds have yet to see. */
  takeLocal(): VisualEvent[] {
    return this.local.splice(0);
  }

  /** Forget what is held (the simulation moved to or from another client). */
  clear(): void {
    this.local = [];
    this.net = [];
  }
}
